import type { FC, SyntheticEvent } from "react";
import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AccordionWrapper,
  DefaultText,
  RouteBody,
  RouteComponentWrapper,
  RouteHeader,
  WorkspaceList,
  SearchInput,
  SearchWrapper,
} from "./route-component.styles";
import { Button } from "@src/components";
import { ReactComponent as AddIcon } from "@src/core/icons/add.svg";
import { Workspace } from "@src/core/services";

import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { APP_ROUTES } from "@src/core/constants";
import {
  StyledDialog,
  StyledDialogContentText,
} from "./components/workspace-menu/workspace-menu.styles";
import { enqueueSnackbar } from "notistack";
import { CustomAccordion } from "./components";

export const RouteComponent: FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { workspaceId } = useParams();

  const [spaceIndex, setSpaceIndex] = useState<number>();
  const [openDialog, setOpenDialog] = useState(false);
  const [spaceIdByClick, setSpaceIdByClick] = useState<string>("");
  const [expanded, setExpanded] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const { data, refetch } = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => Workspace.getAll(),
  });

  const { mutate } = useMutation(Workspace.delete, {
    onSuccess: (response) => {
      enqueueSnackbar("Пространство удалено", {
        variant: "reportComplete",
        className: "success",
        preventDuplicate: true,
      });
      refetch();
    },
    onError: (error: Error) => {
      // код в случае ошибки
    },
  });

  const workspaces = data?.data.items;

  const filteredWorkspaces = useMemo(() => {
    if (!workspaces) return [];
    if (!searchQuery.trim()) return workspaces;
    const q = searchQuery.toLowerCase();
    return workspaces.filter(
      (ws) =>
        ws.name.toLowerCase().includes(q) ||
        ws.routes.some(
          (r) =>
            r.name.toLowerCase().includes(q) ||
            r.method.toLowerCase().includes(q)
        )
    );
  }, [workspaces, searchQuery]);

  const workspaceCreate = () => {
    navigate(`${APP_ROUTES.WORKSPACE}/create`);
  };

  const handleAccordionChange =
    (panel: number) => (isExpanded: SyntheticEvent) => {
      if (
        isExpanded.currentTarget.getAttribute("aria-expanded") &&
        isExpanded.currentTarget.getAttribute("aria-expanded") === "false"
      ) {
        setExpanded((prevExpanded: number[]) => [...prevExpanded, panel]);
      } else if (
        isExpanded.currentTarget.classList.contains("add-method-icon")
      ) {
        setExpanded((prevExpanded: number[]) => [...prevExpanded, panel]);
      } else {
        setExpanded((prevExpanded: number[]) =>
          prevExpanded.filter((item) => item !== panel),
        );
      }
    };

  const handleIconClick = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  const deleteWorkspace = (spaceId: number) => {
    setSpaceIdByClick(spaceId.toString());
    setOpenDialog(true);
  };
  const handleClose = () => {
    setOpenDialog(false);
  };

  const removeHandler = () => {
    if (spaceIdByClick) mutate({ workspaceId: spaceIdByClick });
    setOpenDialog(false);
  };

  useEffect(() => {
    setExpanded(expanded);
  }, [expanded, pathname]);

  // useEffect(() => {
  //   const accordions = document.querySelectorAll(".accordion");

  //   accordions.forEach((accordion) => {
  //     console.log(accordion);
  //     const childDivs = accordion.querySelectorAll("div");
  //     if (accordion.classList.contains("Mui-expanded")) {
  //       childDivs.forEach((div) => {
  //         div.classList.add("Mui-expanded");
  //       });
  //     }
  //   });
  // }, [data]);

  return (
    <RouteComponentWrapper>
      <RouteHeader>
        <h3>Пространства</h3>
        <Button
          text="Создать"
          variant="label"
          borderradius="XXXL"
          icon={<AddIcon />}
          onClick={workspaceCreate}
          disabled={pathname === "/workspace/create"}
        />
      </RouteHeader>
      <SearchWrapper>
        <SearchInput
          placeholder="Поиск по пространствам и маршрутам..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchWrapper>
      <RouteBody>
        {filteredWorkspaces.length === 0 ? (
          <DefaultText>
            Здесь будет отображаться список ваших пространств
          </DefaultText>
        ) : (
          <WorkspaceList>
            {filteredWorkspaces.map((workspace, index) => {
              return (
                <AccordionWrapper
                  key={workspace.id}
                  index={index}
                  lenght={workspaces.length ?? 1000}
                  routeslength={workspace.routes.length}
                  active={
                    Number(workspaceId) === workspace.id ? "true" : "false"
                  }
                  style={
                    Number(workspaceId) === workspace.id
                      ? {
                          background: "white",
                          borderTopRightRadius: "28px",
                          borderBottomRightRadius: "28px",
                          borderTopLeftRadius: "0px",
                          borderBottomLeftRadius: "0px",
                        }
                      : { background: "none", border: "none" }
                  }
                  className={
                    workspace.routes.length === 0 ||
                    Number(workspaceId) === workspace.id
                      ? "Mui-expanded"
                      : ""
                  }
                >
                  <CustomAccordion
                    index={index}
                    spaceIndex={spaceIndex}
                    setSpaceIndex={setSpaceIndex}
                    handleIconClick={handleIconClick}
                    deleteWorkspace={() => deleteWorkspace(workspace.id)}
                    expanded={expanded.includes(workspace.id)}
                    handleAccordionChange={handleAccordionChange(workspace.id)}
                    workspace={workspace}
                    setSpaceIdByClick={setSpaceIdByClick}
                  />
                </AccordionWrapper>
              );
            })}
          </WorkspaceList>
        )}
      </RouteBody>
      <StyledDialog
        open={openDialog}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          backdropFilter: "blur(8px)",
        }}
      >
        <DialogTitle id="alert-dialog-title">Удаление</DialogTitle>
        <DialogContent>
          <StyledDialogContentText id="alert-dialog-description-1">
            Вы уверены, что хотите удалить данные
          </StyledDialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            text="Отмена"
            variant="text"
            borderradius="XXL"
          />
          <Button
            onClick={removeHandler}
            text="Удалить"
            variant="filled"
            autoFocus
            borderradius="XXXL"
          >
            Agree
          </Button>
        </DialogActions>
      </StyledDialog>
    </RouteComponentWrapper>
  );
};
